import { useCallback, useEffect, useRef, useState } from "react"

import { differenceInMinutes } from "date-fns"
import { toast } from "sonner"

import {
  type AdHocStopData,
  useAddAdHocStop,
  useBulkUpdateStopInstances,
  useDeleteStopInstance,
  useSamsaraUpdateRoute,
  useTrackPodUpdateRoute,
  useUpdateRouteInstance,
} from "@/hooks/useCollections"
import {
  describeStagedStops as describeStaged,
  keepUnsavedRoutes,
  removalsToDelete,
  revertStagedRoute,
  stagedStopCountDelta as countDelta,
  withStagedStop,
  withoutStop,
} from "@/lib/stagedRouteEdits"
import { dayOffsetFrom, isPendingStop, pendingStopId } from "@/lib/stagedStops"
import { Location, Route, RouteInstance, Stop, toRouteLegacy } from "@/types/collections"

/**
 * Times and sequence are read off the staged Stop at save, so dragging a staged stop works like a
 * saved one.
 */
export interface PendingStopAdd {
  tempId: string
  routeId: string
  routeDate: Date
  stopData: AdHocStopData
}

interface PendingStopRemoval {
  routeId: string
  stopId: string
}

interface StagedRouteEditsOptions {
  routeInstancesData: RouteInstance[] | undefined
  locations: Location[]
  refetchRoutes: () => Promise<unknown>
  isToday: boolean
  isPastDate: boolean
  /** Holds every route locked: drag, resize, add and cancel all key off the lock. */
  readOnly?: boolean
}

/**
 * Nothing reaches the database, Samsara or Track-POD until save; discard restores the route as at
 * unlock.
 */
export function useStagedRouteEdits({
  routeInstancesData,
  locations,
  refetchRoutes,
  isToday,
  isPastDate,
  readOnly = false,
}: StagedRouteEditsOptions) {
  const updateRouteInstance = useUpdateRouteInstance()
  const bulkUpdateStopInstances = useBulkUpdateStopInstances()
  const addAdHocStopMutation = useAddAdHocStop()
  const deleteStopInstance = useDeleteStopInstance()
  const samsaraUpdateRoute = useSamsaraUpdateRoute()
  const trackpodUpdateRoute = useTrackPodUpdateRoute()

  const [routes, setRoutes] = useState<Route[]>([])

  /**
   * Mirror so the sync effect needn't depend on `unsavedChanges` and reload history on every edit.
   */
  const unsavedRoutesRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (routeInstancesData && routeInstancesData.length > 0) {
      const convertedRoutes = routeInstancesData.map((ri: RouteInstance) => {
        const route = toRouteLegacy(ri)
        return readOnly ? { ...route, isLocked: true } : route
      })
      setRoutes((prevRoutes) =>
        keepUnsavedRoutes(convertedRoutes, prevRoutes, unsavedRoutesRef.current)
      )
    } else if (routeInstancesData) {
      setRoutes([])
    }
  }, [routeInstancesData, readOnly])

  const [unsavedChanges, setUnsavedChanges] = useState<Set<string>>(new Set())
  const [originalRouteStops, setOriginalRouteStops] = useState<Map<string, Stop[]>>(new Map())
  const [pendingStopAdds, setPendingStopAdds] = useState<PendingStopAdd[]>([])
  const [pendingStopRemovals, setPendingStopRemovals] = useState<PendingStopRemoval[]>([])
  const pendingStopSeqRef = useRef(0)

  useEffect(() => {
    unsavedRoutesRef.current = unsavedChanges
  }, [unsavedChanges])

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [routeToLock, setRouteToLock] = useState<string | null>(null)

  const snapshotRoute = useCallback(
    (route: Route) => {
      if (!originalRouteStops.has(route.id)) {
        setOriginalRouteStops((prev) => new Map(prev).set(route.id, [...route.stops]))
      }
    },
    [originalRouteStops]
  )

  const markUnsaved = useCallback((routeId: string) => {
    setUnsavedChanges((prev) => new Set(prev).add(routeId))
  }, [])

  const applyDraggedStops = useCallback(
    (routeId: string, stops: Stop[]) => {
      setRoutes((prevRoutes) => prevRoutes.map((r) => (r.id === routeId ? { ...r, stops } : r)))
      markUnsaved(routeId)
    },
    [markUnsaved]
  )

  const nextPendingStopId = useCallback(() => pendingStopId((pendingStopSeqRef.current += 1)), [])

  const stageStopAdd = useCallback(
    async (route: Route, stagedStop: Stop, pending: PendingStopAdd) => {
      snapshotRoute(route)
      setRoutes((prevRoutes) =>
        prevRoutes.map((r) => (r.id === route.id ? withStagedStop(r, stagedStop) : r))
      )
      setPendingStopAdds((prev) => [...prev, pending])
      markUnsaved(route.id)

      // Must be unlocked for everyone, not just this browser, while the stop is pending.
      if (route.isLocked) {
        try {
          await updateRouteInstance.mutateAsync({
            id: route.id,
            data: { is_locked: false },
          })
        } catch (unlockError) {
          console.error("Error unlocking route for staged stop:", unlockError)
        }
      }
    },
    [snapshotRoute, markUnsaved, updateRouteInstance]
  )

  const stageStopRemoval = useCallback(
    (stop: Stop, route: Route) => {
      snapshotRoute(route)
      setRoutes((prevRoutes) =>
        prevRoutes.map((r) => (r.id === route.id ? withoutStop(r, stop.id) : r))
      )

      if (isPendingStop(stop.id)) {
        // Added and removed in one session: just drop the add.
        setPendingStopAdds((prev) => prev.filter((pending) => pending.tempId !== stop.id))
      } else {
        setPendingStopRemovals((prev) => [...prev, { routeId: route.id, stopId: stop.id }])
      }

      markUnsaved(route.id)
    },
    [snapshotRoute, markUnsaved]
  )

  const forgetUnsavedRoute = useCallback((routeId: string) => {
    setUnsavedChanges((prev) => {
      const next = new Set(prev)
      next.delete(routeId)
      return next
    })
  }, [])

  const handleLockAttempt = useCallback(
    async (routeId: string) => {
      if (unsavedChanges.has(routeId)) {
        setRouteToLock(routeId)
        setConfirmDialogOpen(true)
      } else {
        try {
          await updateRouteInstance.mutateAsync({
            id: routeId,
            data: { is_locked: true },
          })
          setRoutes((prevRoutes) =>
            prevRoutes.map((r) => (r.id === routeId ? { ...r, isLocked: true } : r))
          )
        } catch (error) {
          console.error("Error locking route:", error)
          toast.error("Error", { description: "Failed to lock route. Please try again." })
        }
      }
    },
    [unsavedChanges, updateRouteInstance]
  )

  /**
   * Order matters: removals first, then park existing stops on temp sequences so additions can't
   * collide.
   */
  const persistRouteChanges = useCallback(
    async (route: Route) => {
      const stopsInOrder = [...route.stops].sort((a, b) => a.sequence - b.sequence)
      const savedStops = stopsInOrder.filter((stop) => !isPendingStop(stop.id))
      const stagedStops = stopsInOrder.filter((stop) => isPendingStop(stop.id))

      const updateData: Record<string, unknown> = {
        is_locked: true,
      }

      // Marked modified so schedule changes leave it be.
      if (route.plannedRouteId) {
        updateData.is_modified = true
        updateData.modification_notes = `Modified on ${new Date().toLocaleDateString()} - stop times updated`
        updateData.modified_at = new Date().toISOString()
      }

      // The API derives the depot from the stop it inserts at sequence 1, which a temp-sequence
      // insert never is, so apply it here.
      const firstStop = stopsInOrder[0]
      if (firstStop && isPendingStop(firstStop.id) && firstStop.locationId) {
        const siteName = locations.find((l) => l.id === firstStop.locationId)?.site_name
        if (siteName) updateData.depot = siteName
      }

      await updateRouteInstance.mutateAsync({ id: route.id, data: updateData })

      for (const removal of removalsToDelete(pendingStopRemovals, route)) {
        await deleteStopInstance.mutateAsync(removal.stopId)
      }

      // (route_instance_id, sequence) is unique and a save can reorder or insert, so park
      // everything high first, then settle to 1..n in one request.
      const tempSequenceBase = 10000
      const stagedSequenceBase = 20000

      if (savedStops.length > 0) {
        await bulkUpdateStopInstances.mutateAsync(
          savedStops.map((stop, i) => ({
            id: stop.id,
            data: {
              sequence: tempSequenceBase + i,
            },
          }))
        )
      }

      const createdStops: Array<{ id: string; stop: Stop }> = []
      for (const [i, stop] of stagedStops.entries()) {
        const pending = pendingStopAdds.find((add) => add.tempId === stop.id)
        if (!pending) continue

        const created = await addAdHocStopMutation.mutateAsync({
          routeInstanceId: route.id,
          stopData: {
            ...pending.stopData,
            planned_arrival: stop.plannedArrival.toISOString(),
            planned_departure: stop.plannedDeparture.toISOString(),
            arrival_day_offset: dayOffsetFrom(pending.routeDate, stop.plannedArrival),
            departure_day_offset: dayOffsetFrom(pending.routeDate, stop.plannedDeparture),
            service_time_minutes: differenceInMinutes(stop.plannedDeparture, stop.plannedArrival),
            sequence: stagedSequenceBase + i,
          },
        })

        if (created?.id) createdStops.push({ id: created.id, stop })
      }

      const finalUpdates = [
        ...savedStops.map((stop) => ({ id: stop.id, stop })),
        ...createdStops,
      ].map(({ id, stop }) => ({
        id,
        data: {
          planned_arrival: stop.plannedArrival.toISOString(),
          planned_departure: stop.plannedDeparture.toISOString(),
          sequence: stop.sequence,
        },
      }))

      if (finalUpdates.length > 0) {
        await bulkUpdateStopInstances.mutateAsync(finalUpdates)
      }

      await Promise.allSettled([
        samsaraUpdateRoute
          .mutateAsync(route.id)
          .catch((samsaraError) =>
            console.warn("[TimelineView] Failed to push route changes to Samsara:", samsaraError)
          ),
        trackpodUpdateRoute
          .mutateAsync(route.id)
          .then((result) => {
            // A push Track-POD accepts can still leave stale stops or orders on the driver's list;
            // surface it.
            const stopsWithoutOrder = (result.data?.stopsWithoutOrder as string[] | undefined) ?? []
            const unresolved = [
              ...((result.data?.ordersLeftAlone as string[] | undefined) ?? []),
              ...stopsWithoutOrder.map((number) => `${number}: Track-POD holds no order for it`),
            ]
            if (unresolved.length > 0) {
              toast.warning("Track-POD is not fully in step", {
                description: unresolved.join("; "),
              })
            }
          })
          .catch((trackpodError) => {
            console.warn("[TimelineView] Failed to push route changes to Track-POD:", trackpodError)
            toast.error("Track-POD was not updated", {
              description:
                trackpodError instanceof Error ? trackpodError.message : String(trackpodError),
            })
          }),
      ])
    },
    [
      locations,
      pendingStopAdds,
      pendingStopRemovals,
      updateRouteInstance,
      deleteStopInstance,
      addAdHocStopMutation,
      bulkUpdateStopInstances,
      samsaraUpdateRoute,
      trackpodUpdateRoute,
    ]
  )

  /**
   * Clear the ref with the state: the save refetches immediately and a stale ref would make the
   * merge hide the written stops.
   */
  const clearRouteStagedState = useCallback((routeId: string) => {
    const stillUnsaved = new Set(unsavedRoutesRef.current)
    stillUnsaved.delete(routeId)
    unsavedRoutesRef.current = stillUnsaved

    setUnsavedChanges((prev) => {
      const next = new Set(prev)
      next.delete(routeId)
      return next
    })
    setOriginalRouteStops((prev) => {
      const next = new Map(prev)
      next.delete(routeId)
      return next
    })
    setPendingStopAdds((prev) => prev.filter((add) => add.routeId !== routeId))
    setPendingStopRemovals((prev) => prev.filter((removal) => removal.routeId !== routeId))
  }, [])

  const describeStagedStops = useCallback(
    (routeId: string) => describeStaged(pendingStopAdds, pendingStopRemovals, routeId),
    [pendingStopAdds, pendingStopRemovals]
  )

  const stagedStopCountDelta = useCallback(
    (routeId: string) => countDelta(pendingStopAdds, pendingStopRemovals, routeId),
    [pendingStopAdds, pendingStopRemovals]
  )

  const handleConfirmSave = useCallback(async () => {
    if (isPastDate) {
      toast.error("Cannot edit past dates", {
        description:
          "Route instances can only be edited for today's date. Past dates are read-only.",
      })
      setRouteToLock(null)
      setConfirmDialogOpen(false)
      return
    }

    if (routeToLock) {
      const route = routes.find((r) => r.id === routeToLock)

      if (route) {
        try {
          await persistRouteChanges(route)
        } catch (error) {
          console.error("Error saving route changes:", error)
          toast.error("Error saving changes", {
            description: "Failed to save changes to the database. Please try again.",
          })
          return
        }
      }

      // Clear before refetching so the merge that protects unsaved edits lets the written stops
      // through.
      clearRouteStagedState(routeToLock)

      if (route) {
        await refetchRoutes()

        const modifiedMsg = route.plannedRouteId
          ? " This instance has been marked as modified and won't be overwritten by future schedule changes."
          : ""

        toast.success("Changes saved", {
          description: `Route ${route.code} has been updated, locked, and synced with GPS.${modifiedMsg}`,
        })
      }
    }
    setRouteToLock(null)
    setConfirmDialogOpen(false)
  }, [routeToLock, routes, persistRouteChanges, clearRouteStagedState, refetchRoutes, isPastDate])

  const handleDiscardChanges = useCallback(async () => {
    if (routeToLock) {
      const originalStops = originalRouteStops.get(routeToLock)
      const delta = stagedStopCountDelta(routeToLock)

      try {
        await updateRouteInstance.mutateAsync({
          id: routeToLock,
          data: { is_locked: true },
        })
      } catch (error) {
        console.error("Error locking route after discard:", error)
        toast.error("Error", { description: "Failed to lock route. Please try again." })
      }

      setRoutes((prevRoutes) =>
        prevRoutes.map((r) =>
          r.id === routeToLock ? revertStagedRoute(r, originalStops, delta) : r
        )
      )
      clearRouteStagedState(routeToLock)
    }
    setRouteToLock(null)
    setConfirmDialogOpen(false)
  }, [
    routeToLock,
    originalRouteStops,
    stagedStopCountDelta,
    updateRouteInstance,
    clearRouteStagedState,
  ])

  const handleGlobalSaveChanges = useCallback(async () => {
    if (isPastDate) {
      toast.error("Cannot edit past dates", {
        description:
          "Route instances can only be edited for today's date. Past dates are read-only.",
      })
      return
    }

    const routesToSave = routes.filter((r) => unsavedChanges.has(r.id) && !r.isLocked)
    if (routesToSave.length === 0) return

    try {
      // Clear each as it lands so a mid-way failure leaves the rest staged.
      for (const route of routesToSave) {
        await persistRouteChanges(route)
        clearRouteStagedState(route.id)
      }

      await refetchRoutes()

      const modifiedMsg = routesToSave.some((r) => r.plannedRouteId)
        ? " Modified instances won't be overwritten by future schedule changes."
        : ""

      toast.success("All changes saved", {
        description: `Saved changes for ${routesToSave.length} route${routesToSave.length > 1 ? "s" : ""} and synced with GPS.${modifiedMsg}`,
      })
    } catch (error) {
      console.error("Error saving all route changes:", error)
      await refetchRoutes()
      toast.error("Error saving changes", {
        description: "Failed to save some changes. Please try again.",
      })
    }
  }, [
    routes,
    unsavedChanges,
    persistRouteChanges,
    clearRouteStagedState,
    refetchRoutes,
    isPastDate,
  ])

  const handleGlobalDiscardChanges = useCallback(async () => {
    const routesToDiscard = routes.filter((r) => unsavedChanges.has(r.id) && !r.isLocked)

    setRoutes((prevRoutes) =>
      prevRoutes.map((r) => {
        if (!unsavedChanges.has(r.id) || r.isLocked) return r
        return revertStagedRoute(r, originalRouteStops.get(r.id), stagedStopCountDelta(r.id))
      })
    )

    try {
      for (const route of routesToDiscard) {
        await updateRouteInstance.mutateAsync({
          id: route.id,
          data: { is_locked: true },
        })
      }
    } catch (error) {
      console.error("Error locking routes after discard:", error)
      toast.error("Error", { description: "Failed to lock some routes. Please try again." })
    }

    routesToDiscard.forEach((route) => clearRouteStagedState(route.id))

    toast.success("Changes discarded", {
      description: `Discarded unsaved changes for ${routesToDiscard.length} route${routesToDiscard.length > 1 ? "s" : ""}.`,
    })
  }, [
    routes,
    unsavedChanges,
    originalRouteStops,
    stagedStopCountDelta,
    updateRouteInstance,
    clearRouteStagedState,
  ])

  const toggleRouteLock = async (routeId: string) => {
    // Only today's routes can be locked or unlocked.
    if (!isToday) {
      toast.error("Cannot edit past dates", {
        description:
          "Route instances can only be edited for today's date. Past dates are read-only.",
      })
      return
    }

    const route = routes.find((r) => r.id === routeId)
    if (!route) return

    if (!route.isLocked) {
      handleLockAttempt(routeId)
    } else {
      snapshotRoute(route)
      try {
        await updateRouteInstance.mutateAsync({
          id: routeId,
          data: { is_locked: false },
        })
        setRoutes(routes.map((r) => (r.id === routeId ? { ...r, isLocked: false } : r)))
      } catch (error) {
        console.error("Error unlocking route:", error)
        toast.error("Error", { description: "Failed to unlock route. Please try again." })
      }
    }
  }

  return {
    routes,
    unsavedChanges,
    isSaving: updateRouteInstance.isPending || bulkUpdateStopInstances.isPending,
    snapshotRoute,
    applyDraggedStops,
    nextPendingStopId,
    stageStopAdd,
    stageStopRemoval,
    forgetUnsavedRoute,
    toggleRouteLock,
    handleGlobalSaveChanges,
    handleGlobalDiscardChanges,
    lockConfirmation: {
      open: confirmDialogOpen,
      onOpenChange: setConfirmDialogOpen,
      stagedSummary: routeToLock ? describeStagedStops(routeToLock) : "",
      onSave: handleConfirmSave,
      onDiscard: handleDiscardChanges,
    },
  }
}
